/**
 * React Component Registry Constants
 *
 * Central registry mapping component IDs to React components
 * This is the single source of truth for all React page components
 */

import { ConsentRequestPage } from '../components/pages/consent-request';
import { SettingsPage } from '../components/pages/settings';
import { DataFetchRequestPage } from '../components/pages/data-fetch-request';
import { FINotificationsPage } from '../components/pages/fi-notifications';
import { FipListPage } from '../components/pages/fip-list';
import { PurposePage } from '../components/pages/purpose';
import { AccountAggregatorPage } from '../components/pages/account-aggregator';
import { AdminUsersPage } from '../components/pages/admin-users';
import { FIDataPurgePage } from '../components/pages/fi-data-purge';
import { AnalyticsPortalPageErrorBoundary } from '../components/pages/analytics-portal/AnalyticsPortalPage';
import { BulkMisPage, BulkAllBatchesPage } from '../components/pages/bulk-mis';
import { BulkCreateConsentMisPage } from '../components/pages/bulk-create-consent-mis';
import { EarlyWarningSignalsPage } from '../components/pages/early-warning-signals';
import { DownloadCentrePage } from '../components/pages/download-centre';
import { AnalyticsOnboardingPage } from '../components/pages/analytics-onboarding';
import { PFMPage } from '../components/pages/pfm';
import { SmartRouterDashboardPage } from '../components/pages/smart-router-dashboard';
import { AnalyticsSandboxPage } from '../components/pages/analytics-sandbox';
import { PdfConsolePage } from '../components/pages/pdf-console';
import { VleConsentPage } from '../components/pages/vle-consent';
import { OrchestrationConfigPage } from '../components/pages/orchestration-config';
import type { ComponentType } from 'react';

/**
 * Page Component Type
 * All page components should accept these props from Angular routing
 */
export interface PageComponentProps {
  routeParams?: Record<string, string>;
  queryParams?: Record<string, string>;
  currentRoute?: string;
}

/**
 * Component Registry Entry
 */
export interface ComponentRegistryEntry {
  component: ComponentType<PageComponentProps>;
  displayName: string;
  description?: string;
}

/**
 * Component Registry Map
 * Maps componentId (string) to React component
 *
 * To add a new page:
 * 1. Import the component
 * 2. Add entry to this registry
 * 3. Update Angular registry constants
 * 4. Update Angular routing
 */
export const PAGE_COMPONENT_REGISTRY: Record<string, ComponentRegistryEntry> = {
  // Consent Management Page
  ConsentRequestPage: {
    component: ConsentRequestPage,
    displayName: 'Consent Request Page',
    description: 'Consent management with filters, table, and logs visualization',
  },

  // Settings Page
  SettingsPage: {
    component: SettingsPage,
    displayName: 'Settings Page',
    description: 'Application API key management and configuration',
  },

  // Data Fetch Request Page
  DataFetchRequestPage: {
    component: DataFetchRequestPage,
    displayName: 'Data Fetch Request Page',
    description: 'Data fetch request management with customer account details',
  },

  // FI Notifications Page
  FINotificationsPage: {
    component: FINotificationsPage,
    displayName: 'FI Notifications Page',
    description: 'Financial Information notifications management with account details',
  },

  // Purpose Page
  PurposePage: {
    component: PurposePage,
    displayName: 'Purpose Page',
    description: 'Purpose catalogue with filtering and pagination',
  },

  // FIP List Page
  FipListPage: {
    component: FipListPage,
    displayName: 'FIP List Page',
    description: 'Financial information providers list with status management',
  },

  // Account Aggregator Page
  AccountAggregatorPage: {
    component: AccountAggregatorPage,
    displayName: 'Account Aggregator Page',
    description: 'Account aggregator listing with onboarding controls and auth key management',
  },

  // Admin Users Page
  AdminUsersPage: {
    component: AdminUsersPage,
    displayName: 'Admin Users Page',
    description: 'Admin user management with table, filters, approvals, and bulk upload.',
  },

  // FI Data Purge Page
  FIDataPurgePage: {
    component: FIDataPurgePage,
    displayName: 'FI Data Purge Page',
    description: 'FI data purge monitoring with statistics, filters, and report generation.',
  },

  // Analytics Portal Page
  AnalyticsPortalPage: {
    component: AnalyticsPortalPageErrorBoundary,
    displayName: 'Analytics Portal Page',
    description: 'Analytics dashboard with dynamic charts, data visualization, and nudge management.',
  },

  // Bulk MIS Dashboard Page
  BulkMisPage: {
    component: BulkMisPage,
    displayName: 'Bulk MIS Dashboard',
    description: 'Bulk operations monitoring dashboard with metrics and batch listing',
  },

  // Bulk All Batches Page
  BulkAllBatchesPage: {
    component: BulkAllBatchesPage,
    displayName: 'Bulk All Batches',
    description: 'All batches listing with pagination and ability to view individual records',
  },

  // Early Warning Signals Page
  EarlyWarningSignalsPage: {
    component: EarlyWarningSignalsPage,
    displayName: 'Early Warning Signals',
    description: 'Monitor and manage early warning signals for customer accounts with risk analytics',
  },

  // Download Centre Page
  DownloadCentrePage: {
    component: DownloadCentrePage,
    displayName: 'Download Centre',
    description: 'Download centre for all generated reports and exports',
  },

  // Bulk Create Consent MIS Page
  BulkCreateConsentMisPage: {
    component: BulkCreateConsentMisPage,
    displayName: 'Bulk Create Consent MIS',
    description: 'Bulk consent creation operations monitoring dashboard with metrics and batch listing',
  },

  // Analytics Onboarding Page
  AnalyticsOnboardingPage: {
    component: AnalyticsOnboardingPage,
    displayName: 'Analytics Onboarding',
    description: 'Analytics onboarding wizard with business registration, instance configuration, and attribute exploration',
  },

  // PFM Page
  PFMPage: {
    component: PFMPage,
    displayName: 'PFM - Personal Finance Management',
    description: 'Personal Finance Management mobile app preview with embedded iframe',
  },

  // Smart Router Dashboard Page
  SmartRouterDashboardPage: {
    component: SmartRouterDashboardPage,
    displayName: 'Smart Router Dashboard',
    description: 'Smart Router performance dashboard with traffic distribution, conversion rates, and live status',
  },

  // Analytics Sandbox Page
  AnalyticsSandboxPage: {
    component: AnalyticsSandboxPage,
    displayName: 'Analytics Sandbox',
    description: 'Sandbox environment for experimenting with analytics configurations before production',
  },

  // PDF Console Analytics Page
  PdfConsolePage: {
    component: PdfConsolePage,
    displayName: 'PDF Console Analytics',
    description: 'Run structured analytics on PDF documents and download results as JSON or Excel',
  },

  // VLE Consent Page (CSC Digital Seva)
  VleConsentPage: {
    component: VleConsentPage,
    displayName: 'VLE Consent Dashboard',
    description: 'VLE dashboard for CSC Digital Seva - consent management for Village Level Entrepreneurs',
  },

  // Orchestration Configuration Page
  OrchestrationConfigPage: {
    component: OrchestrationConfigPage,
    displayName: 'Orchestration Configuration',
    description: 'View and manage smart orchestration products and gateway instance configurations',
  },
};

/**
 * Get all registered component IDs
 */
export function getAllComponentIds(): string[] {
  return Object.keys(PAGE_COMPONENT_REGISTRY);
}

/**
 * Check if a component ID is registered
 */
export function isComponentRegistered(componentId: string): boolean {
  return componentId in PAGE_COMPONENT_REGISTRY;
}
